// Loads a creative's version journey and keeps it live: an initial fetch + a debounced refetch
// on every checkpoint signal (CreativeScoreUpdated / CreativeContentUpdated). The debounce keeps
// a coalescing manual-edit burst (one rolling server-side version) from spamming the endpoint.
// Shared by the desktop VersionStepper and the mobile version control.

import { PusherEvent } from '@mondaygpt/constants'
import { debounce } from 'lodash'
import { useEffect, useMemo } from 'react'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { useVersionsStore, type CreativeVersionSummary } from '../../store/versions-store'

interface PusherCreativeRef {
  creativeId?: string
}

// Stable empty snapshot: returning a fresh [] from the selector trips
// useSyncExternalStore's "getSnapshot should be cached" guard on every render.
const EMPTY_VERSIONS: CreativeVersionSummary[] = []

export function useCreativeVersions(creativeId?: string): CreativeVersionSummary[] {
  const versions = useVersionsStore(s =>
    creativeId ? (s.byCreative[creativeId]?.versions ?? EMPTY_VERSIONS) : EMPTY_VERSIONS
  )
  const fetchVersions = useVersionsStore(s => s.fetchVersions)
  const refetch = useMemo(
    () => debounce((id: string) => void fetchVersions(id), 800),
    [fetchVersions]
  )
  useEffect(() => {
    if (creativeId) fetchVersions(creativeId)
  }, [creativeId, fetchVersions])
  useListenToPusherEvent<PusherCreativeRef>(PusherEvent.CreativeScoreUpdated, d => {
    if (creativeId && (!d.creativeId || d.creativeId === creativeId)) refetch(creativeId)
  })
  useListenToPusherEvent<PusherCreativeRef>(PusherEvent.CreativeContentUpdated, d => {
    if (creativeId && d.creativeId === creativeId) refetch(creativeId)
  })
  return versions
}
